import React, { useState, useEffect, useRef } from 'react';
import logo from '../logo.svg';
import './Dashboard.css';
import './Auth.css';
import BibleContent from './BibleContent';
import Auth from './Auth';
import ReadingPlanPanel from './ReadingPlanPanel';
import { loadPlanState, getStreak } from '../utils/readingPlanUtils';
import { bibleApi } from '../api';
import { auth } from '../firebase';
import { reauthenticateWithCredential, EmailAuthProvider, verifyBeforeUpdateEmail, updatePassword } from 'firebase/auth';

function Dashboard({ onNavigateHome, isLightMode, onToggleTheme, user, isSubscribed, onLogout }) {
  const [showAuth, setShowAuth] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [previousVersion, setPreviousVersion] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showReadingPlan, setShowReadingPlan] = useState(false);
  const [targetPassage, setTargetPassage] = useState(null);
  const [readingStreak, setReadingStreak] = useState(() => {
    const s = loadPlanState();
    return s ? getStreak(s.completedDays, s.startDate) : 0;
  });
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [emailChangeError, setEmailChangeError] = useState(null);
  const [emailChangeSent, setEmailChangeSent] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPasswordForPw, setCurrentPasswordForPw] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState(null);
  const [passwordChangeDone, setPasswordChangeDone] = useState(false);
  const userMenuRef = useRef(null);
  const hamburgerMenuRef = useRef(null);

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const response = await bibleApi.getVersions();
        console.log("API Response:", response);
        
        let availableVersions = [];
        if (response && Array.isArray(response.versions)) {
          availableVersions = response.versions;
        } else if (Array.isArray(response)) {
          availableVersions = response;
        }

        // Ensure known versions are always present even if backend hasn't been updated
        const KNOWN_VERSIONS = [
          { id: 'en-bbe',   name: 'BBE',                           language: 'en', local: true },
          { id: 'en-kjv',   name: 'King James Version',            language: 'en', storage_path: 'bibles/en-kjv.json' },
          { id: 'en-web',   name: 'World English Bible',           language: 'en', storage_path: 'bibles/en-web.json' },
          { id: 'zh-cuv',   name: 'Chinese Union Version (Trad.)', language: 'zh', storage_path: 'bibles/zh-cuv.json' },
          { id: 'zh-cuv-s', name: 'Chinese Union Version (Simp.)', language: 'zh-s', storage_path: 'bibles/zh-cuv-s.json' },
          { id: 'fr-lsg',   name: 'Louis Segond 1910',             language: 'fr', storage_path: 'bibles/fr-lsg.json' },
        ];
        KNOWN_VERSIONS.forEach(kv => {
          if (!availableVersions.find(v => v.id === kv.id)) {
            availableVersions.push(kv);
          }
        });

        setVersions(availableVersions);
        
        if (availableVersions.length > 0) {
          const defaultVersion = availableVersions.find(v => v.id === 'en-bbe') || availableVersions[0];
          setSelectedVersion(defaultVersion);
          setPreviousVersion(defaultVersion);
        }
      } catch (error) {
        console.error("Failed to fetch Bible versions:", error);
        setVersions([]);
      }
    };
    fetchVersions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
      if (hamburgerMenuRef.current && !hamburgerMenuRef.current.contains(e.target)) {
        setShowHamburgerMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailChangeError(null);
    const currentUser = auth?.currentUser;
    if (!currentUser) return;

    try {
      // Re-authenticate first (required by Firebase before sensitive operations)
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      // Send verification to new address; email only changes after user clicks the link
      await verifyBeforeUpdateEmail(currentUser, newEmail);
      setEmailChangeSent(true);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setEmailChangeError('Current password is incorrect.');
      } else if (err.code === 'auth/email-already-in-use') {
        setEmailChangeError('That email is already in use by another account.');
      } else if (err.code === 'auth/invalid-email') {
        setEmailChangeError('Please enter a valid email address.');
      } else {
        setEmailChangeError(err.message);
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordChangeError(null);
    if (newPassword !== confirmNewPassword) {
      setPasswordChangeError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordChangeError('Password must be at least 6 characters.');
      return;
    }
    const currentUser = auth?.currentUser;
    if (!currentUser) return;
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPasswordForPw);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setPasswordChangeDone(true);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPasswordChangeError('Current password is incorrect.');
      } else if (err.code === 'auth/weak-password') {
        setPasswordChangeError('Password must be at least 6 characters.');
      } else {
        setPasswordChangeError(err.message);
      }
    }
  };

  const closeChangePassword = () => {
    setShowChangePassword(false);
    setCurrentPasswordForPw('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordChangeError(null);
    setPasswordChangeDone(false);
  };

  const closeChangeEmail = () => {
    setShowChangeEmail(false);
    setNewEmail('');
    setCurrentPassword('');
    setEmailChangeError(null);
    setEmailChangeSent(false);
  };

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    if (Array.isArray(versions)) {
      const firstInLang = versions.find(v => v.language === lang);
      if (firstInLang) {
        setPreviousVersion(selectedVersion);
        setSelectedLanguage(lang);
        setSelectedVersion(firstInLang);
      }
    }
  };

  const handleVersionChange = (e) => {
    const versionId = e.target.value;
    if (Array.isArray(versions)) {
      const version = versions.find(v => v.id === versionId);
      if (version) {
        setPreviousVersion(selectedVersion);
        setSelectedVersion(version);
      }
    }
  };

  // Refresh streak whenever the reading plan panel closes
  useEffect(() => {
    if (!showReadingPlan) {
      const s = loadPlanState();
      setReadingStreak(s ? getStreak(s.completedDays, s.startDate) : 0);
    }
  }, [showReadingPlan]);

  const handleDownloadCancel = () => {
    if (previousVersion) {
      setSelectedVersion(previousVersion);
      setSelectedLanguage(previousVersion.language);
    }
  };

  const handleDownloadSuccess = (newVersion) => {
    setPreviousVersion(newVersion);
  };

  return (
    <div className={`Dashboard ${isLightMode ? 'light-mode' : ''}`}>
      <header className="Dashboard-header">
        <div className="Header-left">
          <div className="Hamburger-wrap" ref={hamburgerMenuRef}>
            <button
              className={`Hamburger-menu${showHamburgerMenu ? ' is-open' : ''}`}
              aria-label="Menu"
              aria-haspopup="true"
              aria-expanded={showHamburgerMenu}
              onClick={() => setShowHamburgerMenu(v => !v)}
            >
              <span className="Hamburger-line"></span>
              <span className="Hamburger-line"></span>
              <span className="Hamburger-line"></span>
            </button>
            {showHamburgerMenu && (
              <div className="Hamburger-dropdown">
                <button
                  className="Hamburger-dropdown-item"
                  onClick={() => { setShowHamburgerMenu(false); onNavigateHome(); }}
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  Home
                </button>
                <button
                  className="Hamburger-dropdown-item Hamburger-dropdown-item--active"
                  onClick={() => setShowHamburgerMenu(false)}
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  Dashboard
                </button>
                <button
                  className="Hamburger-dropdown-item"
                  onClick={() => { setShowHamburgerMenu(false); setShowReadingPlan(true); }}
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  Reading Plan
                </button>
                <a
                  className="Hamburger-dropdown-item Hamburger-dropdown-item--support"
                  href="https://buy.stripe.com/test_3cI7sEbMI25w3gicTk1kA00"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowHamburgerMenu(false)}
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  Support PlutoBible
                </a>
                {!isSubscribed && (
                  <button
                    className="Hamburger-dropdown-item Hamburger-dropdown-item--subscribe"
                    onClick={() => { setShowHamburgerMenu(false); setShowSubscribeModal(true); }}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    Subscribe
                  </button>
                )}
                <div className="Hamburger-divider" />
                {user ? (
                  <>
                    <button
                      className="Hamburger-dropdown-item"
                      onClick={() => { setShowHamburgerMenu(false); setShowChangeEmail(true); }}
                    >
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      Change Email
                    </button>
                    <button
                      className="Hamburger-dropdown-item"
                      onClick={() => { setShowHamburgerMenu(false); setShowChangePassword(true); }}
                    >
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      Change Password
                    </button>
                    <button
                      className="Hamburger-dropdown-item Hamburger-dropdown-item--danger"
                      onClick={() => { setShowHamburgerMenu(false); onLogout(); }}
                    >
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      Logout
                    </button>
                  </>
                ) : (
                  <button
                    className="Hamburger-dropdown-item"
                    onClick={() => { setShowHamburgerMenu(false); setShowAuth(true); }}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                    Login
                  </button>
                )}
              </div>
            )}
          </div>
          <img
            src={logo}
            className="Dashboard-logo"
            alt="PlutoBible Logo"
            onClick={onNavigateHome}
          />
        </div>

        <div className="Header-right">
          {readingStreak > 0 && (
            <button
              className="Streak-badge"
              onClick={() => setShowReadingPlan(true)}
              title="Reading streak — click to open Reading Plan"
            >
              🔥 {readingStreak}
            </button>
          )}
          {user ? (
            <div className="User-profile" ref={userMenuRef}>
              <button
                className="User-email-button"
                onClick={() => setShowUserMenu(v => !v)}
                aria-haspopup="true"
                aria-expanded={showUserMenu}
              >
                {isSubscribed && <span className="User-vip-badge" title="PlutoBible VIP">VIP</span>}
                <span className="User-email">{user.email}</span>
                <span className="User-menu-caret">{showUserMenu ? '▲' : '▼'}</span>
              </button>
              {showUserMenu && (
                <div className="User-dropdown">
                  <button
                    className="User-dropdown-item"
                    onClick={() => { setShowUserMenu(false); setShowChangeEmail(true); }}
                  >
                    Change Email
                  </button>
                  <button
                    className="User-dropdown-item"
                    onClick={() => { setShowUserMenu(false); setShowChangePassword(true); }}
                  >
                    Change Password
                  </button>
                  <button className="User-dropdown-item User-dropdown-item--danger" onClick={onLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="Login-button" onClick={() => setShowAuth(true)}>Login</button>
          )}
          
          <button className="Theme-toggle-small" onClick={onToggleTheme} aria-label="Toggle theme">
            {isLightMode ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            )}
          </button>
        </div>
      </header>

      <main className="Dashboard-content">
        {selectedVersion ? (
          <BibleContent
            user={user}
            isSubscribed={isSubscribed}
            version={selectedVersion}
            onDownloadCancel={handleDownloadCancel}
            onDownloadSuccess={() => handleDownloadSuccess(selectedVersion)}
            versions={versions}
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguageChange}
            onVersionChange={handleVersionChange}
            targetPassage={targetPassage}
          />
        ) : (
          <div className="loading-bible">Loading Bible data...</div>
        )}
      </main>

      {showReadingPlan && (
        <ReadingPlanPanel
          isLightMode={isLightMode}
          language={selectedLanguage}
          onClose={() => setShowReadingPlan(false)}
          onNavigate={(bookIndex, chapterIndex) => {
            setTargetPassage({ bookIndex, chapterIndex });
            setShowReadingPlan(false);
          }}
        />
      )}

      {showAuth && <Auth onClose={() => setShowAuth(false)} />}

      {showChangePassword && (
        <div className="Auth-modal">
          <div className="Auth-content">
            <button className="Close-button" onClick={closeChangePassword}>&times;</button>
            <h2>Change Password</h2>

            {passwordChangeError && <div className="Auth-error">{passwordChangeError}</div>}

            {passwordChangeDone ? (
              <div className="Auth-success">
                Your password has been updated successfully.
              </div>
            ) : (
              <form onSubmit={handleChangePassword}>
                <div className="Form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={currentPasswordForPw}
                    onChange={(e) => setCurrentPasswordForPw(e.target.value)}
                    required
                  />
                </div>
                <div className="Form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="Form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="Submit-button">Update Password</button>
              </form>
            )}
          </div>
        </div>
      )}

      {showChangeEmail && (
        <div className="Auth-modal">
          <div className="Auth-content">
            <button className="Close-button" onClick={closeChangeEmail}>&times;</button>
            <h2>Change Email</h2>

            {emailChangeError && <div className="Auth-error">{emailChangeError}</div>}

            {emailChangeSent ? (
              <div className="Auth-success">
                A verification link has been sent to <strong>{newEmail}</strong>. Your email will update after you click the link.
              </div>
            ) : (
              <form onSubmit={handleChangeEmail}>
                <div className="Form-group">
                  <label>New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="Form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="Submit-button">Send Verification</button>
              </form>
            )}
          </div>
        </div>
      )}

      {showSubscribeModal && (
        <div className="Auth-modal" onClick={() => setShowSubscribeModal(false)}>
          <div className="Subscribe-modal" onClick={e => e.stopPropagation()}>
            <button className="Close-button" onClick={() => setShowSubscribeModal(false)}>&times;</button>
            <div className="Subscribe-icon">
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            </div>
            <h2 className="Subscribe-title">PlutoBible VIP</h2>
            <p className="Subscribe-price">CAD $3 <span>/month</span></p>
            <p className="Subscribe-tagline">Support the app and keep your notes safe across all your devices.</p>
            <ul className="Subscribe-features">
              <li>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                AI Bible chat — 300 messages per month included
              </li>
              <li>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Cloud sync — notes, bookmarks &amp; highlights saved across all devices
              </li>
              <li>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Never lose your data if you switch devices or clear your browser
              </li>
              <li>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Directly supports PlutoBible development
              </li>
            </ul>
            <a
              className="Subscribe-cta"
              href="https://buy.stripe.com/test_7sY00c2c8dOeg344mO1kA01"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowSubscribeModal(false)}
            >
              Subscribe for CAD $3/month
            </a>
            <p className="Subscribe-note">Cancel anytime. Secure payment via Stripe.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
